import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from './firebase';

// Fetch default trial config and return trial fields to add to new user doc
const buildTrialFields = async () => {
  try {
    const snap = await getDoc(doc(db, 'appConfig', 'trialConfig'));
    const months = snap.exists() ? (snap.data().defaultTrialMonths || 0) : 0;
    if (months > 0) {
      const now = new Date();
      const trialEndsAt = new Date(now.getTime() + months * 30 * 24 * 60 * 60 * 1000);
      return { trialUsed: true, trialStartDate: now.toISOString(), trialEndsAt: trialEndsAt.toISOString() };
    }
  } catch (e) { /* config not set yet */ }
  return {};
};

// Register a new user with Firebase Auth + create Firestore profile
export const registerUser = async (email, password, profileData) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const uid = userCredential.user.uid;
  const trialFields = await buildTrialFields();

  const userData = {
    id: uid,
    name: profileData.name,
    firstName: profileData.firstName || '',
    lastName: profileData.lastName || '',
    handle: profileData.handle,
    email: email.toLowerCase(),
    role: 'user',
    status: 'active',
    skillLevel: profileData.skillLevel || 'Beginner',
    handicap: profileData.handicap || 0,
    location: profileData.location || { address: '' },
    followers: [],
    following: [],
    bio: profileData.bio || '',
    birthDate: profileData.birthDate || '',
    gender: profileData.gender || '',
    joinDate: new Date().toISOString(),
    profilePicture: profileData.profilePicture || `https://ui-avatars.com/api/?name=${encodeURIComponent(profileData.name)}&background=16a34a&color=fff&size=128`,
    isCoach: false,
    isPremium: false,
    ...trialFields,
  };

  await setDoc(doc(db, 'users', uid), userData);
  return userData;
};

// Login with email/password, return user profile from Firestore
export const loginUser = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  const uid = userCredential.user.uid;
  const userDoc = await getDoc(doc(db, 'users', uid));

  if (!userDoc.exists()) {
    throw new Error('User profile not found');
  }

  return { id: uid, ...userDoc.data() };
};

// Sign in with Google
export const signInWithGoogle = async () => {
  const result = await signInWithPopup(auth, googleProvider);
  const uid = result.user.uid;
  const userDoc = await getDoc(doc(db, 'users', uid));

  if (userDoc.exists()) {
    return { id: uid, ...userDoc.data(), isNewUser: false };
  } else {
    return {
      id: uid,
      email: result.user.email,
      googleName: result.user.displayName,
      googlePhoto: result.user.photoURL,
      isNewUser: true,
    };
  }
};

// Complete profile for Google sign-in users
export const completeGoogleProfile = async (uid, profileData) => {
  const trialFields = await buildTrialFields();
  const userData = {
    id: uid,
    ...profileData,
    role: 'user',
    status: 'active',
    followers: [],
    following: [],
    joinDate: new Date().toISOString(),
    isCoach: false,
    isPremium: false,
    ...trialFields,
  };
  await setDoc(doc(db, 'users', uid), userData);
  return userData;
};

// Sign out
export const logoutUser = async () => {
  await signOut(auth);
};

// Listen for auth state changes (session persistence)
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      const isEmailPassword = firebaseUser.providerData?.[0]?.providerId === 'password';

      let userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

      // Email/password: Firestore write in registerUser may not have completed yet.
      // Retry up to 5× (2.5 s) before giving up.
      if (!userDoc.exists() && isEmailPassword) {
        for (let i = 0; i < 5; i++) {
          await new Promise(r => setTimeout(r, 500));
          userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
          if (userDoc.exists()) break;
        }
      }

      if (userDoc.exists()) {
        callback({ id: firebaseUser.uid, ...userDoc.data() });
      } else {
        // Google user who hasn't completed their profile yet
        callback({
          id: firebaseUser.uid,
          email: firebaseUser.email,
          name: firebaseUser.displayName || '',
          profileIncomplete: true,
        });
      }
    } else {
      callback(null);
    }
  });
};

// Send password reset email — links to our custom in-app handler
export const resetPassword = async (email) => {
  const actionCodeSettings = {
    url: window.location.origin + '/action',
    handleCodeInApp: true,
  };
  await sendPasswordResetEmail(auth, email, actionCodeSettings);
};

// Change password (requires re-authentication)
export const changePassword = async (currentPassword, newPassword) => {
  const user = auth.currentUser;
  if (!user || !user.email) throw new Error('No authenticated user');
  const credential = EmailAuthProvider.credential(user.email, currentPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
};
